import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  HandThumbUpIcon,
  HandThumbDownIcon,
} from '@heroicons/react/24/solid';
import { apiListApplicationFeedbacks } from '../../services/FeedbackApi';
import {
  TWDataTable,
  TWInput,
  TWSelect,
  EmptyState,
  UserAvatar,
} from '../../components/ui';
import { headerCls, DataTableSkeleton } from '../../data/columns';
import CommentCell from './components/CommentCell';
import { sortableTitle, nextSort } from './components/sortable';

dayjs.extend(utc);

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const LD_OPTIONS = [
  { value: '', label: 'All feedback' },
  { value: 'true', label: 'Likes' },
  { value: 'false', label: 'Dislikes' },
];

const fmtDate = (v) => (v ? dayjs.utc(v).format('MMM D, YYYY') : '—');

const LikeBadge = ({ liked }) =>
  liked ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
      <HandThumbUpIcon className="h-3.5 w-3.5" />
      Like
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-medium text-rose-800 dark:bg-rose-900/30 dark:text-rose-300">
      <HandThumbDownIcon className="h-3.5 w-3.5" />
      Dislike
    </span>
  );

const buildColumns = (sort, onSort) => [
  {
    title: sortableTitle('Store', 'store', sort, onSort),
    key: 'store',
    render: (_, r) => (
      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
        {r.storeName || '—'}
      </span>
    ),
  },
  {
    title: sortableTitle('Customer', 'customer', sort, onSort),
    key: 'customer',
    render: (_, r) => {
      const name = r.customerName || 'Guest';
      return (
        <div className="flex min-w-0 items-center gap-2.5">
          <UserAvatar name={name} size="sm" />
          <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
            {name}
          </span>
        </div>
      );
    },
  },
  {
    title: sortableTitle('Rating', 'rating', sort, onSort),
    key: 'rating',
    render: (_, r) => <LikeBadge liked={r.likeOrDislike} />,
  },
  {
    title: <span className={headerCls}>Comment</span>,
    key: 'comment',
    render: (_, r) => <CommentCell text={r.feedbackText} />,
  },
  {
    title: sortableTitle('Date', 'date', sort, onSort),
    key: 'date',
    render: (_, r) => (
      <span className="text-sm text-slate-600 dark:text-slate-300">
        {fmtDate(r.createDate)}
      </span>
    ),
  },
];

const PlatformRatings = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ld, setLd] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState({ sortBy: 'date', sortOrder: 'desc' });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: PAGE_SIZE,
    totalRecords: 0,
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const fetchRows = useCallback(
    async (pageNumber, likeordislike, searchText, sortState) => {
      setLoading(true);
      try {
        const parts = [
          `?pageNumber=${pageNumber}`,
          `&pageSize=${PAGE_SIZE}`,
          likeordislike !== '' ? `&likeordislike=${likeordislike}` : '',
          searchText ? `&search=${encodeURIComponent(searchText)}` : '',
          `&sortBy=${sortState.sortBy}&sortOrder=${sortState.sortOrder}`,
        ];
        const res = await apiListApplicationFeedbacks(parts.join(''));
        setRows(res.data?.data || []);
        setPagination({
          pageNumber,
          pageSize: PAGE_SIZE,
          totalRecords: res.data?.totalRecords || 0,
        });
      } catch {
        setRows([]);
        message.error('Failed to load platform ratings');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchRows(1, ld, debouncedSearch, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ld, debouncedSearch, sort, fetchRows]);

  const onSort = (key) => setSort((prev) => nextSort(prev, key));

  const isEmpty = !loading && rows.length === 0;
  const columns = buildColumns(sort, onSort);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full max-w-sm">
          <TWInput
            leftIcon={MagnifyingGlassIcon}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer or store…"
            aria-label="Search platform ratings"
          />
        </div>
        <div className="w-full sm:w-44">
          <TWSelect
            value={ld}
            onChange={setLd}
            allowClear={false}
            options={LD_OPTIONS}
            aria-label="Filter by feedback"
          />
        </div>
      </div>

      {loading ? (
        <DataTableSkeleton
          rows={8}
          headers={[
            { label: 'Store', w: 'w-32' },
            { label: 'Customer', shape: 'avatar', w: 'w-32' },
            { label: 'Rating', shape: 'pill', w: 'w-24' },
            { label: 'Comment', w: 'w-40' },
            { label: 'Date', w: 'w-24' },
          ]}
        />
      ) : isEmpty ? (
        <EmptyState
          variant="dashed"
          title="No platform ratings yet"
          description={
            ld !== '' || debouncedSearch
              ? 'No feedback matches your filters.'
              : 'Customer feedback on the app will appear here.'
          }
        />
      ) : (
        <TWDataTable
          columns={columns}
          data={rows}
          rowKey="id"
          pagination={
            pagination.totalRecords > PAGE_SIZE
              ? {
                  current: pagination.pageNumber,
                  pageSize: PAGE_SIZE,
                  total: pagination.totalRecords,
                  showSizeChanger: false,
                  showTotal: (total, range) =>
                    `Showing ${range[0]} to ${range[1]} of ${total} ratings`,
                  onChange: (page) =>
                    fetchRows(page, ld, debouncedSearch, sort),
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default PlatformRatings;
